import {
  ADS_MANAGER_FILTERS,
  CONSTANT_LABELS,
  CONVERSION_PATH_FILTERS,
  CREATIVES_FILTERS,
  ECOMMERCE_TO_GENERAL_LABEL_MAPPING,
  GENERAL_CREATIVES_FILTERS,
  INFLUENCER_FILTERS,
  RT_FILTERS,
  FILTER_TO_API_MAPPING,
} from "@/constants/constants";
import {
  AdsFilterInterface,
  ApiKpiFilterExpression,
  ApiFilterInterface,
  ARRAY_FILTERS,
  type FilterLogic,
  type FilterTypes,
  FilterTypesEnum,
  type KpiFilterExpression,
  type KpiFilterExpressionGroup,
  KpiFilterGroup,
} from "@tracify/shared";
import { availableDashboardModes } from "@lib/hooks/constants";
import { secondsToFilterLabel } from "@lib/util-functions/dates";
import dayjs from "dayjs";

const FILTER_GROUP_SEPARATOR = "\u001c";
const FILTER_EXPRESSION_VALUE_SEPARATOR = "\u001f";
const FILTER_EXPRESSION_VERSION = "v2";

export const createKpiFilterGroup = (
  conditions: AdsFilterInterface[] = []
): KpiFilterGroup => ({
  id: globalThis.crypto.randomUUID(),
  conditions: conditions.map((condition, index) => ({
    ...condition,
    index,
  })),
});

export const createKpiFilterExpressionGroup = (
  conditions: AdsFilterInterface[] = [],
  logic: FilterLogic = "and"
): KpiFilterExpressionGroup => ({
  ...createKpiFilterGroup(conditions),
  logic,
});

export const createKpiFilterExpression = (
  groups: KpiFilterExpressionGroup[] = [],
  logic: FilterLogic = "or"
): KpiFilterExpression => ({ logic, groups });

export const hasKpiFilterConditions = (groups: KpiFilterGroup[]) =>
  groups.some((group) => group.conditions.length > 0);

export const hasKpiFilterExpressionConditions = (
  expression: KpiFilterExpression
) => hasKpiFilterConditions(expression.groups);

const stableConditionValue = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value;
  return [...value].sort((left, right) =>
    JSON.stringify(left).localeCompare(JSON.stringify(right))
  );
};

const canonicalKpiFilterExpression = (expression: KpiFilterExpression) => [
  expression.logic,
  expression.groups
    .filter((group) => group.conditions.length > 0)
    .map((group) =>
      JSON.stringify([
        group.logic,
        group.conditions
          .map((condition) =>
            JSON.stringify([
              condition.name,
              condition.operator,
              stableConditionValue(condition.value),
            ])
          )
          .sort(),
      ])
    )
    .sort(),
];

// Compares semantically: group/condition order and array-value order are ignored.
export const areKpiFilterExpressionsEqual = (
  left: KpiFilterExpression,
  right: KpiFilterExpression
) =>
  JSON.stringify(canonicalKpiFilterExpression(left)) ===
  JSON.stringify(canonicalKpiFilterExpression(right));

export const prependConditionsToFilterGroups = (
  groups: KpiFilterGroup[],
  parentConditions: AdsFilterInterface[],
  allowedNames?: string[]
): KpiFilterGroup[] => {
  const populatedGroups = groups.filter((group) => group.conditions.length > 0);
  const sourceGroups =
    populatedGroups.length > 0 ? populatedGroups : [createKpiFilterGroup()];
  const parentNames = new Set(
    parentConditions.map((condition) => condition.name)
  );

  return sourceGroups.map((group) =>
    createKpiFilterGroup([
      ...parentConditions,
      ...group.conditions.filter(
        (condition) =>
          (!allowedNames || allowedNames.includes(condition.name)) &&
          !parentNames.has(condition.name)
      ),
    ])
  );
};

const formatFilterDateValue = (value: string): string => {
  if (!value) return value;

  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format("MMM DD, YYYY");
};

export const PLURAL_FILTER_MAPPING: Record<string, boolean> = {
  customer_purchase: true,
  purchaseAmount: true,
};

export const formattedFilterType = (
  filterType: FilterTypesEnum,
  plural: boolean
) => {
  if (filterType === FilterTypesEnum.gt) {
    return plural ? "are greater than" : "is greater than";
  } else if (filterType === FilterTypesEnum.lt) {
    return plural ? "are lower than" : "is lower than";
  } else if (filterType === FilterTypesEnum.ne) {
    return plural ? "are not equal to" : "is not equal to";
  } else if (filterType === FilterTypesEnum.icontains) {
    return plural ? "contain" : "contains";
  } else if (filterType === FilterTypesEnum.not_icontains) {
    return plural ? "do not contain" : "does not contain";
  } else if (filterType === FilterTypesEnum.in) {
    return plural ? "are in" : "is in";
  } else if (filterType === FilterTypesEnum.eq) {
    return plural ? "are equal to" : "is equal to";
  } else if (filterType === FilterTypesEnum.in_at) {
    return plural ? "are" : "is";
  } else if (filterType === FilterTypesEnum.contains_any) {
    return plural ? "contain" : "contains";
  } else return `${filterType}`;
};

export const formatFilterToText = (
  filter: AdsFilterInterface,
  position: "start" | "end" | "startExtraSpace",
  dashboardMode = "ecommerce" as (typeof availableDashboardModes)[number]["value"]
) => {
  const isTimeFilterName =
    filter.name === "average_seconds_to_next" ||
    filter.name === "average_seconds_to_conversion";

  const isDateFilterName =
    filter.name === "time" ||
    filter.name === "date" ||
    filter.name === "created_at" ||
    filter.name === "updated_at";

  let filterText = formattedFilterType(
    filter.operator,
    PLURAL_FILTER_MAPPING[filter.name]
  );

  if (isDateFilterName) {
    if (filter.operator === FilterTypesEnum.gt) {
      filterText = "is after";
    } else if (filter.operator === FilterTypesEnum.lt) {
      filterText = "is before";
    }
  }
  const inputLabel =
    position === "startExtraSpace"
      ? `${filter.inputLabel || ""} `
      : position === "start"
        ? filter.inputLabel || ""
        : "";

  let formattedValue =
    ARRAY_FILTERS.includes(filter.operator) || Array.isArray(filter.value)
      ? filter.value
          .map(
            (el: any) =>
              CONSTANT_LABELS[el as keyof typeof CONSTANT_LABELS] ?? el
          )
          .join(", ")
      : (CONSTANT_LABELS[filter.value as keyof typeof CONSTANT_LABELS] ??
        filter.value);

  // Apply date formatting for non-array values only if it's a date filter
  if (!ARRAY_FILTERS.includes(filter.operator) && isDateFilterName) {
    formattedValue = formatFilterDateValue(formattedValue);
  }

  // Format seconds as human-readable duration for time filters
  if (!ARRAY_FILTERS.includes(filter.operator) && isTimeFilterName) {
    formattedValue = secondsToFilterLabel(Number(formattedValue));
  }

  return (
    <span className="text-sm" key={filter.name + filter.index}>
      {dashboardMode === "general"
        ? (ECOMMERCE_TO_GENERAL_LABEL_MAPPING[
            filter.label as keyof typeof ECOMMERCE_TO_GENERAL_LABEL_MAPPING
          ] ?? filter.label)
        : filter.label}{" "}
      <span className="text-foreground-soft">{filterText}</span>{" "}
      <span className="font-semibold">
        {inputLabel}
        {formattedValue}
        {position === "end" ? ` ${filter.inputLabel || ""}` : ""}
      </span>
    </span>
  );
};

export const formatFilterToQuery = (
  filters: AdsFilterInterface[]
): string | null => {
  if (!Array.isArray(filters) || filters.length === 0) return null;
  const filterArray = filters.map((filter) => {
    let value = "";
    try {
      value = ARRAY_FILTERS.includes(filter.operator)
        ? JSON.stringify(filter.value)
        : filter.value;
    } catch (error) {
      value = filter.value;
    }
    return `${filter.name}\u001e${filter.operator}\u001e${value}`;
  });
  return filterArray.join("\u001d");
};

export const formatFilterGroupsToQuery = (
  groups: KpiFilterGroup[]
): string | null => {
  const encodedGroups = groups.flatMap((group) => {
    const encodedGroup = formatFilterToQuery(group.conditions);
    return encodedGroup ? [encodedGroup] : [];
  });

  return encodedGroups.length > 0
    ? encodedGroups.join(FILTER_GROUP_SEPARATOR)
    : null;
};

export const formatKpiFilterExpressionToQuery = (
  expression: KpiFilterExpression
): string | null => {
  const encodedGroups = expression.groups.flatMap((group) => {
    const encodedConditions = formatFilterToQuery(group.conditions);
    return encodedConditions
      ? [
          `${group.logic}${FILTER_EXPRESSION_VALUE_SEPARATOR}${encodedConditions}`,
        ]
      : [];
  });

  if (encodedGroups.length === 0) return null;

  return [FILTER_EXPRESSION_VERSION, expression.logic, ...encodedGroups].join(
    FILTER_GROUP_SEPARATOR
  );
};

export const defaultFilterDefinitions: FilterTypes[] = [
  ...ADS_MANAGER_FILTERS,
  ...RT_FILTERS,
  ...INFLUENCER_FILTERS,
  ...CONVERSION_PATH_FILTERS,
  ...CREATIVES_FILTERS,
  ...GENERAL_CREATIVES_FILTERS,
];

const resolveFilterDetails = (
  filterName: string,
  filterDefinitions: FilterTypes[]
) => {
  const definitionName = filterName.includes("::")
    ? (filterName.split("::").at(-1) ?? filterName)
    : filterName;

  return (
    filterDefinitions.find((filter) => filter.value === definitionName) ??
    filterDefinitions.find((filter) =>
      new RegExp(
        `^${filter.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}_\\d+$`
      ).test(definitionName)
    )
  );
};

export const formatQueryToFilter = (
  filterSearch: string,
  filterDefinitions: FilterTypes[] = defaultFilterDefinitions
) => {
  const splitQuery = filterSearch?.split("\u001d");
  if (!splitQuery?.length) return [];

  const filterArray =
    splitQuery?.map((search, index) => {
      const searchArr = search.split("\u001e");
      if (searchArr.length < 3) {
        return {} as AdsFilterInterface;
      }
      const name = searchArr[0];
      const operator = searchArr[1] as unknown as FilterTypesEnum;
      let value = "";
      try {
        value = ARRAY_FILTERS.includes(operator)
          ? JSON.parse(searchArr[2])
          : searchArr[2];
      } catch (error) {
        value = searchArr[2];
      }

      const filterDetails = resolveFilterDetails(name, filterDefinitions);
      if (!filterDetails) {
        return {} as AdsFilterInterface;
      }
      const queryFilter: AdsFilterInterface = {
        name,
        operator,
        value,
        label: filterDetails.label,
        inputLabel: filterDetails.inputLabel,
        index,
      };
      return queryFilter;
    }) ?? [];
  return filterArray
    .filter((el) => !!el.name)
    .map((el, index) => ({ ...el, index }));
};

export const formatQueryToFilterGroups = (
  filterSearch: string,
  filterDefinitions: FilterTypes[] = defaultFilterDefinitions
): KpiFilterGroup[] => {
  if (!filterSearch) return [];

  return filterSearch
    .split(FILTER_GROUP_SEPARATOR)
    .map((encodedGroup) =>
      createKpiFilterGroup(formatQueryToFilter(encodedGroup, filterDefinitions))
    )
    .filter((group) => group.conditions.length > 0);
};

const isFilterLogic = (value: string): value is FilterLogic =>
  value === "and" || value === "or";

export const formatQueryToKpiFilterExpression = (
  filterSearch: string,
  filterDefinitions: FilterTypes[] = defaultFilterDefinitions
): KpiFilterExpression => {
  if (!filterSearch) return createKpiFilterExpression();

  const expressionParts = filterSearch.split(FILTER_GROUP_SEPARATOR);
  const isVersionedExpression =
    expressionParts[0] === FILTER_EXPRESSION_VERSION &&
    isFilterLogic(expressionParts[1] ?? "");

  if (!isVersionedExpression) {
    return createKpiFilterExpression(
      formatQueryToFilterGroups(filterSearch, filterDefinitions).map((group) =>
        createKpiFilterExpressionGroup(group.conditions)
      )
    );
  }

  const rootLogic = expressionParts[1] as FilterLogic;
  const groups = expressionParts.slice(2).flatMap((encodedGroup) => {
    const separatorIndex = encodedGroup.indexOf(
      FILTER_EXPRESSION_VALUE_SEPARATOR
    );
    if (separatorIndex < 0) return [];

    const groupLogic = encodedGroup.slice(0, separatorIndex);
    if (!isFilterLogic(groupLogic)) return [];

    const conditions = formatQueryToFilter(
      encodedGroup.slice(separatorIndex + 1),
      filterDefinitions
    );
    return conditions.length > 0
      ? [createKpiFilterExpressionGroup(conditions, groupLogic)]
      : [];
  });

  return createKpiFilterExpression(groups, rootLogic);
};

const formatFilterToApiCondition = (
  filter: AdsFilterInterface
): ApiFilterInterface => {
  if (filter.operator === FilterTypesEnum.in_at) {
    const position = filter.name.split("::")[0];
    const name = filter.name.split("::")[1];

    return {
      name,
      operator: filter.operator,
      value: filter.value,
      index: position === "first" ? 0 : position === "last" ? -1 : undefined,
    };
  }

  return {
    name:
      FILTER_TO_API_MAPPING[
        filter.name as keyof typeof FILTER_TO_API_MAPPING
      ] || filter.name,
    operator: filter.operator,
    value:
      filter.operator === FilterTypesEnum.lt ||
      filter.operator === FilterTypesEnum.gt
        ? parseFloat(filter.value)
        : filter.value,
  };
};

export const formatFilterToApiFormat = (filters: AdsFilterInterface[]) => {
  if (!Array.isArray(filters)) return undefined;
  const filterArray = filters.map(formatFilterToApiCondition);
  try {
    return JSON.stringify(filterArray);
  } catch (error) {
    return undefined;
  }
};

export const formatFilterGroupsToApiFormat = (groups: KpiFilterGroup[]) => {
  if (!Array.isArray(groups)) return undefined;

  const clauses = groups.flatMap((group) =>
    group.conditions.length > 0
      ? [
          {
            logic: "and" as const,
            conditions: group.conditions.map(formatFilterToApiCondition),
          },
        ]
      : []
  );

  if (clauses.length === 0) return undefined;

  const expression: ApiKpiFilterExpression = {
    logic: "or",
    clauses,
  };

  try {
    return JSON.stringify(expression);
  } catch (error) {
    return undefined;
  }
};

export const formatKpiFilterExpressionToApiFormat = (
  expression: KpiFilterExpression
) => {
  const clauses = expression.groups.flatMap((group) =>
    group.conditions.length > 0
      ? [
          {
            logic: group.logic,
            conditions: group.conditions.map(formatFilterToApiCondition),
          },
        ]
      : []
  );

  if (clauses.length === 0) return undefined;

  const apiExpression: ApiKpiFilterExpression = {
    logic: expression.logic,
    clauses,
  };

  try {
    return JSON.stringify(apiExpression);
  } catch (error) {
    return undefined;
  }
};

export const conjoinKpiFilterExpressionCondition = (
  expression: KpiFilterExpression,
  condition: AdsFilterInterface
): KpiFilterExpression => {
  const groupsWithoutExistingCondition = expression.groups.flatMap((group) => {
    const conditions = group.conditions.filter(
      (existingCondition) => existingCondition.name !== condition.name
    );
    return conditions.length > 0
      ? [createKpiFilterExpressionGroup(conditions, group.logic)]
      : [];
  });

  if (expression.logic === "and") {
    return createKpiFilterExpression(
      [
        ...groupsWithoutExistingCondition,
        createKpiFilterExpressionGroup([condition]),
      ],
      "and"
    );
  }

  if (groupsWithoutExistingCondition.length === 0) {
    return createKpiFilterExpression([
      createKpiFilterExpressionGroup([condition]),
    ]);
  }

  return createKpiFilterExpression(
    groupsWithoutExistingCondition.flatMap((group) => {
      if (group.logic === "and") {
        return [
          createKpiFilterExpressionGroup([...group.conditions, condition]),
        ];
      }

      return group.conditions.map((groupCondition) =>
        createKpiFilterExpressionGroup([groupCondition, condition])
      );
    })
  );
};

export const prependConditionsToFilterExpression = (
  expression: KpiFilterExpression,
  parentConditions: AdsFilterInterface[],
  allowedNames?: string[]
): KpiFilterExpression => {
  const parentNames = new Set(
    parentConditions.map((condition) => condition.name)
  );
  const scopedGroups = expression.groups.flatMap((group) => {
    const conditions = group.conditions.filter(
      (condition) =>
        (!allowedNames || allowedNames.includes(condition.name)) &&
        !parentNames.has(condition.name)
    );

    return conditions.length > 0
      ? [createKpiFilterExpressionGroup(conditions, group.logic)]
      : [];
  });

  return parentConditions.reduce(
    conjoinKpiFilterExpressionCondition,
    createKpiFilterExpression(scopedGroups, expression.logic)
  );
};
